import { useEffect, useMemo, useRef, useState } from "react";
import { Lock, FileText, MapPin, CheckCircle2, ChevronDown, ChevronUp, Database } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SOLAR_CITIES, HOMEBUILDER_CITIES, SOLAR_TOTAL_FIELDS, HOMEBUILDER_TOTAL_FIELDS, type CityPreview } from "@/data/cityPreviewData";
import { ONBOARDING_STAGES } from "@/data/onboardingData";
import { HOMEBUILDER_STAGES } from "@/data/homebuilderData";
import { cn } from "@/lib/utils";

interface Props {
  industryId?: string;
  selectedKeys?: Set<string>;
}

// Map every known data-point key to a single category bucket.
const KEY_TO_CATEGORY: Record<string, string> = {};
const KEY_TO_LABEL: Record<string, string> = {};
(() => {
  const map = KEY_TO_CATEGORY;
  const allStages = [...ONBOARDING_STAGES, ...HOMEBUILDER_STAGES];
  for (const stage of allStages) {
    for (const dp of stage.dataPoints) KEY_TO_LABEL[dp.key] = dp.label;
  }

  // Subsection-driven buckets first (most specific).
  for (const stage of allStages) {
    for (const sub of stage.subsections ?? []) {
      const bucket =
        sub.label === "Interconnection" ? "Utility / IX" : sub.label; // Electrical, Structural, Permitting, Interconnection
      for (const k of sub.keys) map[k] = bucket;
    }
  }

  // Stage-driven buckets for homebuilder steps.
  const HB_STAGE_BUCKET: Record<string, string> = {
    "hb-site-plan": "Zoning",
    "hb-floor-electrical": "Electrical",
    "hb-structural-section": "Structural",
    "hb-architectural": "Zoning",
    "hb-approvals": "Permitting",
  };

  for (const stage of allStages) {
    for (const dp of stage.dataPoints) {
      if (map[dp.key]) continue; // already assigned via subsection
      const label = dp.label.toLowerCase();
      if (/fire/.test(label)) { map[dp.key] = "Fire"; continue; }
      if (HB_STAGE_BUCKET[stage.id]) { map[dp.key] = HB_STAGE_BUCKET[stage.id]; continue; }
      if (dp.jurisdiction === "utility") { map[dp.key] = "Utility / IX"; continue; }
      if (stage.id === "approvals") { map[dp.key] = "Permitting"; continue; }
      if (dp.jurisdiction === "city" || dp.jurisdiction === "county") { map[dp.key] = "Zoning"; continue; }
      // state-level fallback based on label keywords
      if (/wind|snow|load|structural|engineer/.test(label)) { map[dp.key] = "Structural"; continue; }
      if (/electric|meter|disconnect|ground|inverter|conduit|gfci|receptacle|panel|line diagram/.test(label)) { map[dp.key] = "Electrical"; continue; }
      map[dp.key] = "Zoning";
    }
  }
  return map;
})();

const CATEGORY_ORDER = ["Zoning", "Fire", "Electrical", "Structural", "Permitting", "Utility / IX"];

function useCountUp(target: number, duration = 600, key?: string | number) {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, key]);
  return val;
}

function StatNumber({ value, cityKey }: { value: number; cityKey: string }) {
  const v = useCountUp(value, 700, cityKey);
  return <span className="tabular-nums">{v.toLocaleString()}</span>;
}

export function CityDataPreview({ industryId, selectedKeys }: Props) {
  const cities = industryId === "residential-builder" ? HOMEBUILDER_CITIES : SOLAR_CITIES;
  const [activeId, setActiveId] = useState<string>(cities[0].id);
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const city: CityPreview = cities.find((c) => c.id === activeId) ?? cities[0];

  const selectedLabels = useMemo(() => {
    if (!selectedKeys) return [] as string[];
    return Array.from(selectedKeys)
      .map((k) => KEY_TO_LABEL[k])
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [selectedKeys]);

  const selectedCategoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!selectedKeys) return counts;
    for (const key of selectedKeys) {
      const cat = KEY_TO_CATEGORY[key];
      if (!cat) continue;
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [selectedKeys]);

  const orderedSelectedCats = CATEGORY_ORDER.filter((c) => selectedCategoryCounts[c] > 0);


  const remainingSelected = Math.max(0, selectedLabels.length - city.unlockedRows.length);

  const isHomebuilder = industryId === "residential-builder";
  const totalFields = isHomebuilder ? HOMEBUILDER_TOTAL_FIELDS : SOLAR_TOTAL_FIELDS;
  const industryNoun = isHomebuilder ? "residential building" : "solar & storage";

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-2xl border bg-card overflow-hidden shadow-sm">
        {/* City picker + cited-sources badge */}
        <div className="flex flex-wrap items-center gap-2 px-5 sm:px-6 pt-5 pb-4 border-b bg-muted/30">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground mr-1">
            Pick a city
            <span className="ml-1.5 normal-case tracking-normal text-muted-foreground/80">
              · {cities.length} of <span className="tabular-nums font-semibold text-foreground">28,000+</span> U.S. jurisdictions
            </span>
          </span>
          {cities.map((c) => {
            const active = c.id === activeId;
            return (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors border",
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-accent",
                )}
              >
                <MapPin className="h-3.5 w-3.5" />
                {c.name}, {c.state}
              </button>
            );
          })}
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
            <FileText className="h-3.5 w-3.5 text-primary" />
            <span className="tabular-nums text-foreground font-semibold">
              <StatNumber value={city.stats.sources} cityKey={city.id} />
            </span>
            cited sources
          </span>
        </div>

        {/* Category chips — counts reflect the user's selected data points */}
        {orderedSelectedCats.length > 0 && (
          <div
            key={`chips-${activeId}`}
            className="flex flex-wrap items-center gap-2 px-5 sm:px-6 py-4 border-t border-b bg-muted/20 animate-fade-in"
            style={{ animationDelay: "60ms", animationFillMode: "both" }}
          >
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground mr-1">
              Your selections
            </span>
            {orderedSelectedCats.map((cat, i) => (
              <span
                key={`${activeId}-${cat}`}
                className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium animate-fade-in"
                style={{ animationDelay: `${120 + i * 50}ms`, animationFillMode: "both" }}
              >
                {cat}
                <span className="text-muted-foreground tabular-nums">
                  {selectedCategoryCounts[cat]}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Scale strip — frames the table as a sample of a much larger dataset */}
        <div
          key={`scale-${activeId}`}
          className="flex items-start gap-3 px-5 sm:px-6 py-3 border-t bg-muted/30 animate-fade-in"
          style={{ animationDelay: "240ms", animationFillMode: "both" }}
        >
          <Database className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            {selectedLabels.length > 0 ? (
              <>
                <span className="text-foreground">
                  Showing <span className="tabular-nums font-semibold">{Math.min(city.unlockedRows.length, selectedLabels.length)}</span> of your <span className="tabular-nums font-semibold">{selectedLabels.length}</span> selections for {city.name}.
                </span>{" "}
                <span className="text-muted-foreground">
                  We track <span className="font-medium text-foreground">{totalFields}+ {industryNoun} data points</span> per jurisdiction — and <span className="font-semibold text-primary">update them when codes change</span>.
                </span>
              </>
            ) : (
              <>
                <span className="font-semibold text-foreground">{totalFields}+ {industryNoun} data points</span>
                <span className="text-muted-foreground"> per jurisdiction — and counting.</span>
              </>
            )}
          </div>
        </div>

        {/* Row-level keyframes (scoped to this component) */}
        <style>{`
          @keyframes fordje-fly-in {
            0%   { opacity: 0; transform: translateX(-28px) scale(0.98); }
            60%  { opacity: 1; transform: translateX(2px) scale(1.005); }
            100% { opacity: 1; transform: translateX(0) scale(1); }
          }
        `}</style>

        {/* Data table */}
        <div className="px-5 sm:px-6 py-5">
          <div className="grid grid-cols-12 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pb-2 border-b">
            <div className="col-span-5 sm:col-span-4">Field</div>
            <div className="col-span-5 sm:col-span-6">Value</div>
            <div className="col-span-2 hidden sm:block">Source</div>
            <div className="col-span-2 sm:hidden text-right">Src</div>
          </div>

          {city.unlockedRows.map((row, i) => {
            const rowKey = `${city.id}::${row.field}`;
            const isExpanded = expandedRows.has(rowKey);
            const hasDetails = !!row.details;
            const delay = 340 + i * 90;
            return (
              <div
                key={`${activeId}-${row.field}`}
                className="border-b border-border/60 -mx-5 sm:-mx-6 px-5 sm:px-6"
                style={{ animation: `fordje-fly-in 420ms ease-out ${delay}ms both` }}
              >
                <div
                  className={cn(
                    "grid grid-cols-12 items-center py-2.5 text-sm",
                    hasDetails && "cursor-pointer hover:bg-muted/30 transition-colors rounded-sm",
                  )}
                  onClick={() => {
                    if (!hasDetails) return;
                    setExpandedRows((prev) => {
                      const next = new Set(prev);
                      if (next.has(rowKey)) next.delete(rowKey);
                      else next.add(rowKey);
                      return next;
                    });
                  }}
                >
                  <div className="col-span-5 sm:col-span-4 text-muted-foreground pr-2 flex items-center gap-1.5 min-w-0">
                    {hasDetails && (
                      isExpanded
                        ? <ChevronUp className="h-3.5 w-3.5 text-primary shrink-0" />
                        : <ChevronDown className="h-3.5 w-3.5 text-primary shrink-0" />
                    )}
                    <span className="truncate">{row.field}</span>
                  </div>
                  <div className="col-span-5 sm:col-span-6 font-medium pr-2">{row.value}</div>
                  <div className="col-span-2 text-right sm:text-left">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex items-center gap-1 text-xs text-primary cursor-help"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FileText className="h-3 w-3" />
                          <span className="hidden sm:inline truncate max-w-[120px]">{row.citation}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        <p className="text-xs">{row.citation}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                {hasDetails && isExpanded && (
                  <div className="col-span-12 pb-3 pl-5 sm:pl-6">
                    <ul className="text-xs text-muted-foreground space-y-1 border-l-2 border-primary/30 pl-3">
                      {row.details!.split("\n").map((line, i) => (
                        <li key={i} className="leading-relaxed">{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}

          {/* Locked preview */}
          {city.lockedRows.length > 0 && (
            <div
              key={`locked-${activeId}`}
              className="grid grid-cols-12 items-center py-2.5 text-sm border-b border-border/60 animate-fade-in"
              style={{
                animationDelay: `${340 + city.unlockedRows.length * 90 + 200}ms`,
                animationFillMode: "both",
              }}
            >
              <div className="col-span-5 sm:col-span-4 text-muted-foreground pr-2 truncate">
                {city.lockedRows[0].field}
              </div>
              <div className="col-span-7 sm:col-span-8 pr-2">
                <span
                  className="inline-block h-3.5 w-full max-w-[220px] rounded-sm bg-muted-foreground/25"
                  style={{ filter: "blur(2px)" }}
                />
              </div>
            </div>
          )}


          {/* All selected data points — covered for this city */}
          {selectedLabels.length > 0 && (
            <div className="pt-4">
              <button
                type="button"
                onClick={() => setShowAllSelected((v) => !v)}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                {showAllSelected ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    Hide your {selectedLabels.length} selected data points
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    Show {remainingSelected.toLocaleString()} more for {city.name}
                  </>
                )}
              </button>

              {showAllSelected && (
                <div className="mt-3 rounded-lg border bg-muted/20 p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {selectedLabels.map((label) => (
                      <div key={label} className="flex items-center gap-2 text-xs text-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="truncate">{label}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] text-muted-foreground text-center">
                    Every data point you selected is tracked for {city.name}, with citations.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </TooltipProvider>
  );
}
